/**
 * CurrencyApiClient
 * 
 * Handles currency-related operations and formatting
 */

import { BaseApiClient } from './BaseApiClient';
import { currencyService, CurrencyDetails } from '../currencyService';

export class CurrencyApiClient extends BaseApiClient {
  /**
   * Get currency details
   */
  async getCurrencyDetails(code: string): Promise<CurrencyDetails> {
    return currencyService.getCurrencyDetails(code);
  }

  /**
   * Format currency amount
   */
  async formatCurrency(amount: number, currencyCode: string): Promise<string> {
    return currencyService.formatCurrency(amount, currencyCode);
  }

  /**
   * Preload commonly used currencies
   */
  async preloadCurrencies(currencyCodes: string[]): Promise<void> {
    return currencyService.preloadCurrencies(currencyCodes);
  }

  /**
   * Clear currency cache
   */
  clearCurrencyCache(): void {
    currencyService.clearCache();
  }

  /**
   * Get currency cache statistics
   */
  getCurrencyCacheStats() {
    return currencyService.getCacheStats();
  }

  /**
   * Get supported currencies
   */
  async getSupportedCurrencies(): Promise<string[]> {
    return this.get<string[]>('/currencies/supported');
  }

  /**
   * Get the global central-bank fiat currency catalog.
   *
   * GET /api/v1/currencies/cb — public, static ISO 4217–style catalog
   * keyed by uppercase code: `{ currencies: { USD: { name, code, symbol, decimals }, ... } }`.
   * Returns the uppercase currency codes.
   */
  async getCentralBankFiatCurrencies(): Promise<string[]> {
    const response = await this.get<{
      currencies?: Record<string, { code?: string }>;
    }>('/currencies/cb');
    const currencies = response?.currencies || {};
    return Object.keys(currencies).map((code) => code.toUpperCase());
  }

  /**
   * Get currency exchange rates
   */
  async getExchangeRates(baseCurrency: string = 'USD'): Promise<Record<string, number>> {
    return this.get<Record<string, number>>(`/currencies/exchange-rates?base=${baseCurrency}`);
  }

  /**
   * Convert currency amount
   */
  async convertCurrency(amount: number, fromCurrency: string, toCurrency: string): Promise<{
    originalAmount: number;
    convertedAmount: number;
    fromCurrency: string;
    toCurrency: string;
    exchangeRate: number;
    lastUpdated: string;
  }> {
    return this.post<{
      originalAmount: number;
      convertedAmount: number;
      fromCurrency: string;
      toCurrency: string;
      exchangeRate: number;
      lastUpdated: string;
    }>('/currencies/convert', { amount, fromCurrency, toCurrency });
  }

  /**
   * Get currency historical data
   */
  async getCurrencyHistory(currencyCode: string, days: number = 30): Promise<{
    currency: string;
    history: Array<{
      date: string;
      rate: number;
      change: number;
      changePercent: number;
    }>;
  }> {
    return this.get<{
      currency: string;
      history: Array<{
        date: string;
        rate: number;
        change: number;
        changePercent: number;
      }>;
    }>(`/currencies/${currencyCode}/history?days=${days}`);
  }

  /**
   * Get currency trends
   */
  async getCurrencyTrends(currencyCode: string): Promise<{
    currency: string;
    trends: {
      '24h': { change: number; changePercent: number };
      '7d': { change: number; changePercent: number };
      '30d': { change: number; changePercent: number };
    };
  }> {
    return this.get<{
      currency: string;
      trends: {
        '24h': { change: number; changePercent: number };
        '7d': { change: number; changePercent: number };
        '30d': { change: number; changePercent: number };
      };
    }>(`/currencies/${currencyCode}/trends`);
  }

  /**
   * Get user's preferred currency
   */
  async getUserPreferredCurrency(): Promise<{ currency: string }> {
    return this.get<{ currency: string }>('/user/preferred-currency');
  }

  /**
   * Set user's preferred currency
   */
  async setUserPreferredCurrency(currency: string): Promise<{ success: boolean; message: string }> {
    return this.post<{ success: boolean; message: string }>('/user/preferred-currency', { currency });
  }

  /**
   * Get currency availability by region
   */
  async getCurrencyAvailability(region?: string): Promise<{
    region: string;
    availableCurrencies: string[];
    recommendedCurrencies: string[];
    restrictedCurrencies: string[];
  }> {
    const params = region ? `?region=${region}` : '';
    return this.get<{
      region: string;
      availableCurrencies: string[];
      recommendedCurrencies: string[];
      restrictedCurrencies: string[];
    }>(`/currencies/availability${params}`);
  }

  /**
   * Get acceptable currencies for payment methods
   */
  private cachedCurrencies: {
    paypal: string[];
    stripe: string[];
    bank: string[];
    bitcoin: string[];
    all: string[];
  } | null = null;

  private defaultCurrencies = {
    paypal: ['USD', 'EUR', 'GBP', 'CAD', 'AUD'],
    stripe: ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY', 'CHF'],
    bank: ['USD', 'EUR', 'GBP', 'CAD'],
    bitcoin: ['BTC', 'USD', 'EUR'],
    all: ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY', 'CHF', 'BTC']
  };

  async getAcceptableCurrencies(): Promise<{
    paypal: string[];
    stripe: string[];
    bank: string[];
    bitcoin: string[];
    all: string[];
  }> {
    // Return cached currencies if available
    if (this.cachedCurrencies) {
      return this.cachedCurrencies;
    }

    try {
      const response = await this.get<{
        all: string[];
        currencies: string[];
        default: string;
      }>('/currency/acceptable');
      
      // Backend returns { all: string[], currencies: string[], default: string }
      // Transform to expected format
      const allCurrencies = (response.all || response.currencies || ['USD']).map((c) =>
        String(c).toUpperCase()
      );

      // Backend may omit CHF / send lowercase codes; Stripe still charges those fiats.
      const result = {
        paypal: allCurrencies.filter(c => this.defaultCurrencies.paypal.includes(c)),
        stripe: [...new Set([...this.defaultCurrencies.stripe, ...allCurrencies.filter(c => this.defaultCurrencies.stripe.includes(c))])],
        bank: allCurrencies.filter(c => this.defaultCurrencies.bank.includes(c)),
        bitcoin: allCurrencies.filter(c => this.defaultCurrencies.bitcoin.includes(c)),
        all: [...new Set([...allCurrencies, ...this.defaultCurrencies.stripe])]
      };
      
      this.cachedCurrencies = result;
      return result;
    } catch (error) {
      // Fallback to default currencies if backend endpoint not available
      console.warn('⚠️ Using default acceptable currencies - backend endpoint error:', error);
      return this.defaultCurrencies;
    }
  }

  // Add method to clear the cache
  clearAcceptableCurrenciesCache(): void {
    this.cachedCurrencies = null;
  }

  /** GET `/api/v1/currency` — issuer MeMoney row (platform merges defaults). */
  async getCurrencyMeMoneyIssuer(): Promise<unknown> {
    return this.get<unknown>('/currency');
  }

  /** POST `/api/v1/currency/branding/upload` — MM$ icon / background (non–merchant-files path). */
  async uploadCurrencyBranding(payload: {
    type: 'icon' | 'background';
    filename: string;
    mimetype: string;
    size: number;
    data: string;
  }): Promise<unknown> {
    return this.post<unknown>('/currency/branding/upload', payload);
  }
} 